import Dataset from '../model/dataset'

import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import Immutable from 'immutable'
import { Map } from 'immutable'
import appDisplay from 'scoreboard/components/app-display'
import cleanObject from 'scoreboard/components/clean-object'
import config from 'scoreboard/components/config'
import LoadingDots from 'scoreboard/components/loading-dots'
import selects from 'scoreboard/components/selects'
import i18n from 'scoreboard/components/i18n'
import icon from 'scoreboard/components/icon'
import DatasetTreeItemSelector from '../records/dataset-tree-item-selector'
import filterText from '../filters/filter-text'
import nextLinkGroup from '../filters/next-link-group'
import PermissionsByFilterPanel from './permissions-by-filter-panel'
import permissionsUtil from './permissions-util'
import renderFieldDropdown from '../render-field-dropdown'
import DatasetsTableExpander from './datasets-table-expander'
import DataType from 'scoreboard/datasets/model/data-type'
const { ROLLUP } = DataType
import template from './permissions-by-filter.hbs'

const principalKey = p => `${p.id}_${p.type}`

/**
 * The prefix for template select options, suffixed with `fieldId`
 * @type {string}
 */
const TEMPLATE_PREFIX = 'rtt_'
const getTemplateSelectOptionId = fieldId => `${TEMPLATE_PREFIX}${fieldId}`
/**
 * This component is visible when a user toggles the "By Permissions Filter"
 * option when editing dataset permissions.
 *
 * There are three principal panels, but new permissions can only be added to
 * these panels:
 * (1) Direct permissions
 * (2) View all permissions
 * Indirect permissions are only shown for rollup tree selections and are
 * read-only
 *
 * All state changes that are made in this component are mapped back to the
 * TR's in the principal panel from the "By User or Group" view.  Likewise, any
 * changes made to that panel are synchronized over to this component.
 *
 * Because the filter selections are not persisted anywhere there a few model
 * properties that are used to maintain the previous client state when a save
 * is performed, they are all prefixed with 'permissionFilter'
 */
const PermissionsByFilter = Backbone.View.extend({

	className: 'permissions-by-filter',
	/** @type {Dataset} */
	model: null,

	// These need names that don't collide with model attributes
	fieldSelector: 'select[name=permissionFilterField]',
	valueSelector: 'select[name=permissionFilterValue]',

	/** @type {Number} the id of the field that is currently selected */
	_fieldId: Dataset.ANY_FILTER,
	/** @type {Filter} The currently selected filter object (1st select) */
	_filterIdentity: undefined,
	/** The currently selected tree template field option, if any (1st select) */
	_rollupTreeGroupFieldId: undefined,
	/** The currently selected filter values (2nd select) */
	_filterValue: undefined,

	events: function() {
		const events = {}
		events['change ' + this.fieldSelector] = '_onField',
		events['change ' + this.valueSelector] = '_onFilterValue'
		events['click .rollup-button'] = '_onRollupTree'
		return events
	},

	// Setup internal state

	/**
	 *
	 * @param {Function(JQuery row)} options.principalDataRowFn
	 *
	 * @param {Function(id,type,fieldId)} options.principalModelFn
	 * Given a principal id/type return the current permissions state contained
	 * in the main principal panel.  Tree templates also require fieldId.
	 *
	 */
	initialize(options = {}) {
		this.options = options

		this._IN_ROLLUP_TREE = {
			value: 'IN_ROLLUP_TREE',
			label: i18n.get('datasets.permissions.restrictiveFilter.inRollupTree'),
			selected: true,
			group: i18n.get('common.all')
		}

		this.viewState = this.options.viewState

		this._readModel()
	},

	_readModel: function() {
		const {permissions,
			// The following are set on the model to maintain state after a save
			permissionFilterField,
			permissionFilterValue,
			permissionFilterRollupTreeId,
			permissionFilterRollupTreeLabel} = this.model.attributes

		this._fieldId = _.defaultTo(permissionFilterField, this._fieldId)
		/**
		 * The currently selected filter value; the index position that can
		 * be used to find the corresponding Filter Identity object
		 */
		this._filterValue = permissionFilterValue
		/** If a rollup filter was applied, this is the tree selection */
		this._rollupTreeId = permissionFilterRollupTreeId
		/** If a rollup filter was applied, this is the tree label */
		this._rollupTreeLabel = permissionFilterRollupTreeLabel

		/**
		 * Principals that have a direct permission to a specific filter
		 * @type Immutable.Map<Filter, Principal[]>
		 */
		this._principalsByFilterIdentity = new Map().asMutable()
		/**
		 * Key is a dataset fieldId
		 * Value is an array of objects {filter, principal} each representing
		 * a tree template assigned to the field
		 */
		this._permissionByRollupTemplateField = {}
		/**
		 * Principals with permission to the dataset
		 * having no filters (so they can view all records)
		 * @type {Principal[]}
		 */
		this._viewAllPrincipals = {}
		/**
		 * @type {Object} A POJO that maps filters by fieldId
		 * keys - {Number} fieldId
		 * values - {Filter} (ImmutableJS) Filter objects based on their
		 * distinct attributes (detached from permissions)
		 */
		this._distinctFiltersByFieldId = {}
		/**
		 * There could be duplicates if multiple filters exist for the same
		 * field, but `renderFieldDropdown` turns them into a set so we don't
		 * need to address it here.
		 *
		 * @type {Number[]} The fieldIds that have filters (to include in
		 *     the field select)
		 */
		this._allowedFieldIds = []
		/**
		 * @type {Number[]} The visibleLinkedFieldIds that have filters
		 *     (to include in the field select)
		 */
		this._visibleLinkedFieldIds = []

		_.each(permissions, p => this._readPermission(p))
		this._readFields()
	},

	/**
	 * @returns {{filter, principal}[]} of dataset permissions that are
	 * tree templates for the selected field in the dropdown.
	 * (e.g. 'Subject UIC: Rollup Permissions Template')
	 */
	_getRollupTreeGroupPermissionsForField() {
		const id = this._rollupTreeGroupFieldId
		return id && this._permissionByRollupTemplateField[id] || []
	},

	/**
	 * This is the combination of a fieldId and a tree template that we
	 * represent as a principal for the purpose of showing in the principal
	 * panel when a
	 * 'rollup field: Rollup Permissions Template' option is selected
	 */
	_getRollupTreeGroupPrincipalsForField() {
		return _.map(this._getRollupTreeGroupPermissionsForField(), permission => {
			const {principal, filter} = permission
			this.model.assignBuiltInIsAnyValue(principal, filter)
			return principal
		})
	},

	_readPermission: function (permission) {
		const {filters, principal} = permission
		_.each(filters, filter => this._buildContext(filter, principal))
		if (_.isEmpty(filters)) {
			this._viewAllPrincipals[principalKey(principal)] = principal
		}
	},

	/**
	 * Inspect a filter permission for a given principal and setup the internal
	 * state.
	 */
	_buildContext: function (filter, principal) {

		if (this._readFilterForRollupTemplate(filter, principal)) {
			// built-in filters for rollup templates are displayed independently
			return true
		}

		const {fieldId, visibleLinkedFieldId} = filter
		const {dataType} = this._field(fieldId)
		const fieldNames = config.datasetFilterProperties[dataType]
		const filterIdentity = Immutable.fromJS(cleanObject(
			// The server gives us the field names each data-type uses
			// (so any temporary form fields of a different name are excluded)
			// but we have to exclude the properties that make them all distinct
			// https://github.com/SpiderStrategies/Scoreboard/pull/26393#pullrequestreview-292670764
			_.omit(_.pick(filter, fieldNames), ['id', 'permissionId', 'datasetId', 'linkGroup']),
			{nonEmpty: true}))

		const filters = this._distinctFiltersByFieldId[fieldId] || []
		let filterKey = _.find(filters, f => f.equals(filterIdentity))

		// Don't have this exact filter yet, so add it
		if (!filterKey) {
			filterKey = filterIdentity
			filters.push(filterKey)
			if (visibleLinkedFieldId) {
				this._visibleLinkedFieldIds.push(visibleLinkedFieldId)
			} else if (this._includesFieldFilters(filters, principal)) {
				// Don't show the field if it's a rollup field with only the
				// built-in template filter
				this._allowedFieldIds.push(fieldId)
			}
			this._distinctFiltersByFieldId[fieldId] = filters
		}

		const principals = this._principalsByFilterIdentity.get(filterKey)
		if (principals) {
			principals.push(principal)
		} else {
			this._principalsByFilterIdentity.set(filterKey, [principal])
		}
	},

	/**
	 * @returns The new `this._permissionByRollupTemplateField` entry if this
	 * was a built-in rollup template filter, otherwise false
	 */
	_readFilterForRollupTemplate: function(filter, principal) {
		const {fieldId} = principal
		if (this.model.isFilterBuiltInRollupTemplateFilter(filter, principal)) {
			const permission = {principal, filter}
			let permissions = this._permissionByRollupTemplateField[fieldId] || []
			permissions.push(permission)
			this._permissionByRollupTemplateField[fieldId] = permissions
			return permission
		}
		return false
	},

	_readFields: function() {
		this._fieldsWithValues = _.map(this._distinctFiltersByFieldId,
			(_,fieldId) => this._field(fieldId))
	},

	/**
	 * If the only filter is a built-in rollup filter for a template return
	 * false, otherwise true.
	 */
	_includesFieldFilters(filters=[], principal) {
		return _.size(filters) > 1 || !this.model.isFilterBuiltInRollupTemplateFilter(filters[0], principal)
	},

	// State Inspection

	/**
	 * @returns {Principal[]} The principals that have atleast one filter
	 * that is directly associated to the dataset.
	 */
	_getPrincipalsWithDirectPermission: function() {
		const principals = new Immutable.Set().asMutable()
		this._principalsByFilterIdentity.forEach(principalArray => {
			_.each(principalArray, p => principals.add(p))
		})
		_.each(this._permissionByRollupTemplateField, permissions => {
			_.each(permissions, ({principal}) => {
				principals.add(principal)
			})
		})
		return principals.toJSON()
	},

	/**
	 * @returns {Object} the field object for a provided fieldId, or the
	 *     currently selected field.  Handles resolving visibleLinkedFieldIds,
	 *     and returns the underlying field if it's a dataset-link.
	 */
	_field: function(fieldId) {
		const field = this.model.getFieldByFieldIdentifier(fieldId || this._fieldId)
		return this.model.getUnderlyingField(field)
	},

	/**
	 * @returns {boolean} If the selected field is a rollup tree
	 */
	_isRollup: function() {
		return _.get(this._field(), 'dataType') === ROLLUP
	},

	/**
	 * @returns {boolean} If the selected filter value is an is-any or
	 *     is-not-any, indicating the the tree selection elements must be
	 *     visible.
	 */
	_isRollupTreeFilter: function() {
		return this._isRollup() &&
			(this._IN_ROLLUP_TREE.value == this._filterValue ||
				// No visible filters means only tree filters, just show the button
				_.isEmpty(this._getVisibleFilters()))
	},

	_isRollupTreeGroupSelectedAsField: function() {
		return !_.isNil(this._rollupTreeGroupFieldId)
	},

	/**
	 * Returns the filters that should be available for the user to select.

	 * Rollup tree filters on is-any or is-not-any are suppressed because
	 * we show the rollup tree selector instead.
	 *
	 * @returns {string[]}
	 * @private
	 */
	_getVisibleFilters: function() {
		return _.filter(this._fieldValueFilters, f => {
			const operator = f.get('operator')
			return !this._isRollup() || (operator !== 'is-any' && operator !== 'is-not-any')
		})
	},

	/**
	 * Get the select options to show for filter values.
	 *
	 * This is typically all filters, except for Rollup fields which exclude
	 * all 'is-any' and 'is-not-any' filter values, but adds an extra
	 * 'in rollup tree' option.
	 */
	_getFilterValueOptions: function() {
		const field = this._field()
		const filters = this._getVisibleFilters()
		let options = []
		if (this._isRollup() && !_.isEmpty(filters)) {
			options = [this._IN_ROLLUP_TREE]
		}
		return options.concat(_.map(filters, (f, i) => {
			return {
				value: i,
				label: filterText(this.model, f.toJSON(), field, this.options.treeItemsById, {
					showBuiltInRollupTreeGroupLabel: true
				})
			}
		}))
	},

	/**
	 * When the user is viewing a rollup tree template for dataset field
	 * they are only able to add other templates as principals, no users/groups
	 */
	_potentialAssigneeUrl: function(includeRollupTreeGroups = false) {
		const params = $.param({
			includeRollupTreeGroups,
			rollupTreeGroupFieldId: includeRollupTreeGroups ? this._rollupTreeGroupFieldId : undefined,
			includeViewOnly: true,
			includeActiveViewers: true
		})
		return `api/datasets/${this.model.id}/potential-assignees?${params}`
	},

	_includeRollupTreeGroupsForField: function(fieldId = this._fieldId) {
		// No template principal support for linked fields
		// https://github.com/SpiderStrategies/Scoreboard/issues/35287#issuecomment-773481073
		return !Dataset.isVisibleLinkedFieldIdentifier(fieldId)
	},

	// Rendering

	render: function() {
		this._removeChildren()
		this.$el.empty().append(template())
		this._fieldsSelect = this.$(this.fieldSelector)
		this._valuesSelect = this.$(this.valueSelector)
		this._rollupItem = this.$('.rollup-item')
		this._rollupButton = this.$('.rollup-button')

		this.loadingDots = new LoadingDots({ el: this.$('.loading-dots-container') })

		const fieldIds = _.keys(this._permissionByRollupTemplateField)
		const extraOptions = _.map(fieldIds, fieldId => ({
			id: getTemplateSelectOptionId(fieldId),
			label: i18n.get('datasets.rollupTreeGroup.forField', this._field(fieldId).label)
		}))

		renderFieldDropdown({
			el: this._fieldsSelect,
			dataset: this.model,
			selectedId: this._fieldId,
			allowedFieldIds: this._allowedFieldIds,
			allowedVisibleLinkedFieldIds: this._visibleLinkedFieldIds,
			includeAnyFilter: true,
			defaultGroup: i18n.get('datasets.fields'),
			extraOptions
		})

		// writeToDom is going to apply the previous value, so we have to figure
		// out the right values before that happens
		this._updateFilterValueOptions()
		this.refreshSelects()
		/*
		 * When the form is saved the entire dataset-edit.js view is re-rendered.
		 * _readModel extracts the previous state of this form so we can maintain
		 * the same selections.  For this reason, the panels can not be rendered
		 * until the state is applied to the selects
		 */
		this._renderPanels()

		// #27254 - Can't add a principal to the ANY filter (the default on initial load)
		if (Dataset.ANY_FILTER == this._fieldId) {
			this.directPanel.toggleAddPrincipal(false)
		}

		this._toggleRollupElements()

		return this
	},

	/**
	 * Renders the panels, taking into account which principals are shown
	 * based on the selected filter.
	 */
	_renderPanels: function() {
		_.invokeMap(this.panels, 'remove')

		// If this.viewState is defined, that means a viewState has been passed down
		// to this view and we should build things according to it, so it should
		// override this.previousViewState
		if (this.viewState) {
			this.previousViewState = this.viewState
		}

		this.viewAllPanel = this._createFilterPanel({
			id: 'viewAll',
			headerKey: 'datasets.permissions.viewAll',
			currentPrincipals: _.values(this._viewAllPrincipals),
			excludePrincipals: this._getPrincipalsWithDirectPermission(),
			viewState: _.get(this.previousViewState, 'viewAll'),
			assigneesUrl: this._potentialAssigneeUrl(false)
		}, false)

		let currentPrincipals = []
		if (!this._isRollupTreeFilter() && this._filterIdentity) {
			currentPrincipals = this._principalsByFilterIdentity.get(this._filterIdentity)
		} else if (this._isRollupTreeGroupSelectedAsField()) {
			currentPrincipals = this._getRollupTreeGroupPrincipalsForField()
		}

		this.directPanel = this._createFilterPanel({
			id: 'direct',
			headerKey: 'admin.group.permission.direct',
			placeholderKey: 'datasets.addUserGroupOrRTG',
			currentPrincipals,
			viewState: _.get(this.previousViewState, 'direct'),
			assigneesUrl: this._potentialAssigneeUrl(this._includeRollupTreeGroupsForField()),
			includeEveryone: () => !this._isRollupTreeGroupSelectedAsField()
		})
		this.directPanel.on('before-add', (principal) => {
			// New principals need to have the rollup type select added
			// so we decorate the principal with the default here
			if (this._isRollupTreeFilter()) {
				principal.rollupPermission = 'item-rollups-and-descendants'
			}
		})
		this.directPanel.on('change:rollup-permission', this._onRollupPermission.bind(this))

		this.indirectPanel = this._createFilterPanel({
			id: 'indirect',
			headerKey: 'datasets.permissions.indirect',
			viewState: _.get(this.previousViewState, 'indirect'),
			renderTemplateRollupSelect: false
		})
		this.indirectPanel.toggleAddPrincipal(false) // Doesn't make sense to add indirect permissions
		this.indirectPanel.toggleVisible(this._isRollupTreeFilter())

		this.panels = [this.viewAllPanel, this.directPanel, this.indirectPanel]
		this.trigger('render:panels')

		// https://github.com/SpiderStrategies/Scoreboard/issues/27410
		let scrollTop = _.get(this.previousViewState, 'scrollTop')
		if (!_.isNil(scrollTop)) {
			this.directPanel.on('expand', () => {
				this.options.scrollContainer.scrollTop(scrollTop)
				delete this.previousViewState
				delete this.viewState
			})
		} else {
			delete this.previousViewState
			delete this.viewState
		}
	},

	_createFilterPanel: function(options, readFilter=true) {
		let view = new PermissionsByFilterPanel(_.extend({
			model: this.model,
			currentPrincipals: [],
			principalDataRowFn: this.options.principalDataRowFn
		}, options))
		this.$el.append(view.el)
		view.render()

		var recreateExpander = view => {
			view.tableExpander.remove()
			delete view.tableExpander
			view.tableExpander = new DatasetsTableExpander({
				table: view.$('table'),
				onToggleExpandTable: () => this._resizePanel(view),
				maxRows: this.options.maxRows,
				scrollContainer: this.options.scrollContainer
			}).setup()
			this._resizePanel(view)
		}

		view.on('before-add', (principal, newTr) => {
			this._addPrincipal(readFilter, principal, newTr)
		})
		view.on('add', () => recreateExpander(view))
		view.on('updatePrincipals', () => recreateExpander(view))

		view.on('edit', this.trigger.bind(this, 'edit'))
		view.on('delete', principal => {
			this.trigger('delete', principal)
			this._deleteOwnPrincipal(principal)
		})

		view.tableExpander = new DatasetsTableExpander({
			table: view.$('table'),
			isCollapsed: _.result(options.viewState, 'tableCollapsed'),
			// If we're expanding/collapsing one of the tables within the .permissions-by-filter
			// accordion sections, be sure to resize that section given the new
			// size of the table.
			onToggleExpandTable: () => this._resizePanel(view),
			maxRows: this.options.maxRows,
			scrollContainer: this.options.scrollContainer
		}).setup()
		this._resizePanel(view)

		return view
	},

	/**
	 * Triggered by the panels when the user adds a new principal.
	 *
	 * @param {Boolean} [readFilter=false] true indicates this is a direct
	 *     permission and the state of the filter selects should be applied as
	 *     a new permission
	 * @param {Principal} principal
	 * @param {jQuery} newTr The row that was just added to the panel.
	 */
	_addPrincipal: function(readFilter=false, principal) {
		// Add to the 'by User or Group' panel first
		if (_.isEmpty(this.options.principalDataRowFn(principal))) {
			// But only if the principal isn't already there
			// (because they have one or more filters already)
			this.trigger('before-add', principal)
		}

		// Now add the current filter selection to the permission
		const modelRow = this.options.principalDataRowFn(principal)

		// Add new permission if one doesn't exist
		const {permissions = {principal, filters: []}} = modelRow.data()
		const newFilters = readFilter ? this._readFiltersFromDom(principal) : []
		// Add filter to any existing filters, MUST go at the end or duplicate key exceptions you will get.
		// Use _.compact() to make sure we aren't appending onto an empty array or an array
		// with missing values.
		this.trigger('add:filter', newFilters)
		permissions.filters = _.concat(_.compact(permissions.filters), newFilters)
		modelRow.data('permissions', permissions)

		this._readPermission(permissions)
		this._excludePrincipal(principal)
	},

	/**
	 * Expand the direct permission principals.
	 * This is a separate step because the resize doesn't happen correctly
	 * when this view is not visible (at render time)
	 */
	showDirectPermissions: function() {
		this.directPanel.toggle(true)
	},

	// Updates / Side Effects from other Principal Panels

	/**
	 * Updates this view when a principal is added to the "By User or Group"
	 * panel
	 */
	syncPrincipal: function(principal, filters = []) {
		this._readPermission({principal, filters})
		this._excludePrincipal(principal)
		this._rerender()
	},

	/**
	 * Updates view when a principal is deleted from the "By User or Group"
	 * Panel.
	 */
	deletePrincipal: function(principal) {
		this._deleteOwnPrincipal(principal)
		this._rerender()
	},

	/**
	 * Updates internal state of this view when a principal is deleted
	 * from one of `this.panels` due to a user clicking the delete button for
	 * a single principal.
	 */
	_deleteOwnPrincipal: function(principal) {
		this._removePrincipalFromFilters(principal)
		// #27408, #27409 - Don't re-render due to the complexities of rollups
		// We know the filter wasn't changed so we can just stop here
	},

	/**
	 * remove a principal from the list of potential options (because it has
	 * been selected elsewhere)
	 */
	_excludePrincipal: function(principal) {
		_.each(this.panels, p => {
			// Must not be able to select this principal in another panel
			_.invoke(p.principalPanel.principalChooser, 'excludePrincipal', principal)
		})
	},

	/**
	 * Reads current state and re-renders everything.
	 * Used when a principal is added or deleted.
	 */
	_rerender: function () {
		// If there is now a filter that has no principals, remove it from the options
		_.each(this._distinctFiltersByFieldId, filters => {
			_.remove(filters, f => {
				return _.isEmpty(this._principalsByFilterIdentity.get(f))
			})
		})
		this._readFields() // Field options may have changed
		// Save the collapsed state of the panels/tables before going on to re-render them
		// so we can maintain the same state.
		this.previousViewState = this.getViewState()
		this.render()
		this.refreshSelects()
	},

	_resizePanel: function(panel) {
		panel.toggleTransitions(false)
		panel.resize()
		process.nextTick(() => panel.toggleTransitions(true))
	},

	// Resize all three panels without using the expanding/collapsing transition.
	resizePanels: function() {
		_.forEach(this.panels, p => this._resizePanel(p))
	},

	/**
	 * Invoked when principal settings are changed (but not yet saved) from the
	 * main principal panel (By User or Group toggle option), allowing
	 * principals to be moved among the three panels based on the new
	 * permissions/filters.
	 * (When the form is saved, it is re-rendered)
	 *
	 * @param permission The updated permission
	 */
	reloadPermission: function(permission) {
		const {principal} = permission
		const {type} = principal

		// These are a special case, they don't need reloaded
		if (type === 'rollup-tree-group') {
			return
		}

		this._removePrincipalFromFilters(principal)
		this._readPermission(permission)
		this._rerender()
	},

	/**
	 * Applies the current field and filter selections and makes sure the
	 * appropriate elements are visible.
	 */
	refreshSelects: function() {
		const val = this._rollupTreeGroupFieldId ?
			getTemplateSelectOptionId(this._rollupTreeGroupFieldId) :
			this.fieldId
		selects.val(this._fieldsSelect, val)
		this._onField()
		const valueIndex = _.findIndex(this._fieldValueFilters,
			f => f.equals(this._filterIdentity))
		if (valueIndex > -1) {
			selects.val(this._valuesSelect, valueIndex)
			this._valuesSelect.removeClass('is-not-displayed')
		}
	},

	/**
	 * Display or hide the "Indirect" panel and tree selection button
	 * based on whether or not the current filter is a rollup with "is-any"
	 * filters
	 */
	_toggleRollupElements: function() {
		const treeFilter = this._isRollupTreeFilter()
		const showDirectPanelAddButton = Dataset.ANY_FILTER != this._fieldId &&
			!!(!treeFilter || this._rollupTreeId)

		_.invoke(this.indirectPanel, 'toggleVisible', treeFilter)
		_.invoke(this.directPanel, 'toggleAddPrincipal', showDirectPanelAddButton)
		this._rollupItem.toggleClass('is-not-displayed', !treeFilter)
		this._setRollupTreeLabel()
	},

	_setRollupTreeLabel: function() {
		if (this._rollupTreeLabel) {
			this._rollupButton.html(
				_.escape(this._rollupTreeLabel)
				+ icon('edit'))
		}
	},

	/**
	 * Update the internal state when a principal is deleted,
	 * or their permissions change.  This keeps each panel in sync with each other.
	 * @param principal
	 * @private
	 */
	_removePrincipalFromFilters: function (principal) {
		delete this._viewAllPrincipals[principalKey(principal)]

		// "Normal" Filter
		this._principalsByFilterIdentity.forEach(principalArray =>
			_.remove(principalArray, p => permissionsUtil.isEqualPrincipal(p, principal)))

		// Rollup Template Filters
		const {fieldId} = principal
		if (fieldId) {
			_.remove(this._permissionByRollupTemplateField[fieldId], permission =>
				permissionsUtil.isEqualPrincipal(permission.principal, principal))
		}
	},

	// Form Interactions (Select change events)

	/**
	 * Applies the value from the field select (1st Dropdown)
	 *
	 * Can be because either
	 * (1) The user selected a new dataset field to filter permissions by
	 * (2) We are (re)rendering
	 *
	 * NOTE: _.invoke used on Panels here because they won't have been rendered
	 * yet on initial load.  The state is required to be read in and applied
	 * before they are rendered so that the multi-principal-chooser is correct.
	 */
	_onField: function(evt) {
		let fieldValue = selects.val(this._fieldsSelect)
		let placeHolderText = this._includeRollupTreeGroupsForField(fieldValue) ?
			// Special case: show the template principal as direct permission
			i18n.get('datasets.addUserGroupOrRTG') :
			// Unless it's a linked field and we don't include RPTs
			i18n.get('admin.group.addUserGroup')

		if (_.startsWith(fieldValue, TEMPLATE_PREFIX)) {
			const [prefix, fieldId] = fieldValue.split('_')
			this._fieldId = undefined
			this._rollupTreeGroupFieldId = fieldId
			this._updatePanelsForFieldSelection(evt)
			const principals = this._getRollupTreeGroupPrincipalsForField()
			_.invoke(this.directPanel, 'updatePrincipals', principals)
			placeHolderText = i18n.get('datasets.addRTG')
		} else if (fieldValue) {
			this._fieldId = fieldValue
			this._rollupTreeGroupFieldId = undefined
			this._updatePanelsForFieldSelection(evt)
		}
		_.invoke(this.directPanel, 'setPlaceHolderText', placeHolderText)
	},

	_updatePanelsForFieldSelection: function (evt) {
		// must be before any/clear check next
		this._updateFilterValueOptions()
		this._verifyTreeSelection()

		const treeFilter = this._isRollupTreeFilter()
		if (!treeFilter) {
			this.model.unset('permissionFilterRollupTreeId', {silent: true})
		}

		const anyFilter = Dataset.ANY_FILTER == this._fieldId
		const clearFilter = _.isEmpty(this._fieldId) || anyFilter
		if (clearFilter) {
			delete this._filterValue
			delete this._filterIdentity
			this._updateDirectPanel(anyFilter, evt)
		} else {
			this._onFilterValue(evt) // trigger an update to the principals
		}
		this._toggleRollupElements() // must be after current field selection is applied

		if (treeFilter) {
			this._previousRollupFieldId = this._fieldId
		}
	},

	/**
	 * Update the principal listing for the direcdt panel based on the
	 * selection state.
	 */
	_updateDirectPanel: function (isAnyFilter, evt) {
		if (this._isRollupTreeGroupSelectedAsField()) {
			// We can't call this._onFilterValue when a tree template is selected
			// but we do need to capture the state and re-render the panels
			this._onFilterStateChangeEvent(evt)
		} else {
			const directPrincipals = () => isAnyFilter ?
				this._getPrincipalsWithDirectPermission() : []

			if (this.directPanel) {
				this.directPanel.updatePrincipals(directPrincipals())
			} else {
				// for initial rendering (#27114)
				this.once('render:panels',
					() => this.directPanel.updatePrincipals(directPrincipals()))
			}
		}
	},

	/**
	 * (re)renders the filter value select options based on the filter
	 * selected in the 1st dropdown.
	 */
	_updateFilterValueOptions: function () {
		const id = this._getFieldIdForFieldSelection()
		this._fieldValueFilters = this._distinctFiltersByFieldId[id]
		const showValues = this._doesFilterRequireValueSelect()
		this.$('.filter-values').toggleClass('is-not-displayed', !showValues)

		if (showValues) {
			selects(this._valuesSelect, {
				options: this._getFilterValueOptions(),
				escapeLabels: false
			})
		}
	},

	/**
	 * Gets the field for which the filter values can be retrieved from after
	 * the user has selected a value in the "Field" dropdown.
	 */
	_getFieldIdForFieldSelection: function () {
		let resolvedId = this._fieldId
		// #27390 - Don't get the underlying field for linked fields (only VLFs)
		if (Dataset.isVisibleLinkedFieldIdentifier(this._fieldId)) {
			// fieldId could be synthetic (e.g. -3 = Any Filter), thus the safe call here
			resolvedId = _.result(this, '_field.id', resolvedId)  // #27246
		}
		return resolvedId
	},

	/**
	 * @returns {boolean} true if the filter value select should be shown
	 */
	_doesFilterRequireValueSelect: function() {
		let showValues = false
		if (_.isEmpty(this._fieldId)) {
			showValues = false
		} else if (this._isRollup()) {
			showValues = !_.isEmpty(this._getVisibleFilters())
		} else if (this._fieldId != Dataset.ANY_FILTER) {
			showValues = true
		}
		return showValues
	},

	/**
	 * The user selected a distinct filter value (2nd dropdown)
	 */
	_onFilterValue: function(evt) {

		if (evt || !this._filterValue) {
			this._filterValue = this._getSelectedFilterValue()
		}

		this._verifyTreeSelection()
		this._updatePrincipalsForFilterSelection()
		this._toggleRollupElements()
		this._onFilterStateChangeEvent(evt)

		if (this._isRollupTreeFilter()) {
			this._previousRollupFieldId = this._fieldId
		}
	},

	/**
	 * Invoked when a filter was changed by the user in such a way that
	 * the a state change was triggered in the panels.
	 * @param evt
	 */
	_onFilterStateChangeEvent: function (evt) {
		if (evt) {
			// This is a result of a user interaction
			// re-rendering panels here is cleaner than trying to manage
			// the state of each multi-principal-chooser whenever a new filter
			// selection is made
			this.previousViewState = this.getViewState()
			this._renderPanels()
		}
	},

	_getSelectedFilterValue() {
		return selects.val(this._valuesSelect)
	},

	_updatePrincipalsForFilterSelection: function () {
		if (this._isRollupTreeFilter() &&
			!this._fetchRollupPrincipals()) {
			// No tree selection yet, so no direct permissions
			_.invoke(this.directPanel, 'updatePrincipals', [])
			_.invoke(this.directPanel, 'toggleAddPrincipal', false)
		} else if (this._filterValue) {
			this._assignFilterIdentityFromSelection()
			const principals = this._principalsByFilterIdentity.get(this._filterIdentity)
			_.invoke(this.directPanel, 'updatePrincipals', principals)
		}
	},

	/**
	 * Updates the current filter identity based on the values the user has
	 * selected
	 */
	_assignFilterIdentityFromSelection() {
		this._filterIdentity = this._getVisibleFilters()[this._filterValue]
	},

	/**
	 * User clicked the rollup tree button, present a dialog with the tree
	 */
	_onRollupTree: function() {
		const {treeId} = this._field()
		this._treeItemSelector = new DatasetTreeItemSelector({
			id: treeId,
			selectedId: this._rollupTreeId
		})
		this._treeItemSelector.on('select', this._assignTreeItemSelection.bind(this))
		appDisplay.dialog(this._treeItemSelector)
	},

	_verifyTreeSelection() {
		if (this._isRollupTreeFilter() &&
			this._previousRollupFieldId != null &&
			this._previousRollupFieldId !== this._fieldId) {
			// #27921 - New rollup, must clear the previous tree item
			this._assignTreeItemSelection({
				id: undefined,
				label: i18n.get('datasets.permissions.restrictiveFilter.selectRollupItem')
			})
		}
	},

	/**
	 * To be called when the user has made a tree item selection or the
	 * previous selection is being cleared.
	 *
	 * @param {Number|String} [treeItem.id] Pass undefined if the tree selection
	 * should be reset and the user should see the "edit" button.
	 *
	 * @param {String} treeItem.label An i18n'd label
	 */
	_assignTreeItemSelection: function(treeItem) {
		const {id, label} = treeItem
		const hasSelection = !_.isUndefined(id)
		// #27382 - Need to make the treeItem available for the filter-badge
		if (hasSelection) {
			this.options.treeItemsById[id] = treeItem
		}
		this._rollupTreeId = id
		this._rollupTreeLabel = label
		this.model.set('permissionFilterRollupTreeId', id, {silent: true})
		this.model.set('permissionFilterRollupTreeLabel', label, {silent: true})
		this._setRollupTreeLabel()
		this.directPanel.toggleAddPrincipal(hasSelection)
		this._fetchRollupPrincipals()
	},

	/**
	 * When the user selects a rollupPermission option on a given row
	 * @param {jQuery} options.modelRow
	 * @param {Principal} options.principal The principal object for the row
	 * @param {String} options.rollupPermission The selected value
	 */
	_onRollupPermission: function({modelRow, principal, rollupPermission}) {
		const {permissions} = modelRow.data()
		const {filterId, type} = principal
		let filter
		let treeId = this._rollupTreeId
		if (filterId) {
			// This is an update to an existing filter so can find it by id
			filter = _.find(permissions.filters, {id: filterId})
		} else if (type === Dataset.ROLLUP_TREE_GROUP) {
			// It's not an additional filter on the built-in (because no filterId)
			// So we must be updating the build-in filter rollup value
			treeId = principal.treeRootId
			filter = _.find(permissions.filters, Dataset.isBuiltInFilter)
		} else {
			// Otherwise, if there is no filterId it's because it hasn't been persisted yet
			// Just take the first one we find without a filterId that matches
			// the current field and tree selection
			filter = _.find(permissions.filters,
				f => _.isUndefined(f.id) &&
					f.fieldId == this._fieldId &&
					_.get(f, `rollupIsAnyFilters[${this._rollupTreeId}]`)
			)
		}
		filter.rollupIsAnyFilters[treeId] = rollupPermission
	},

	/**
	 * @returns {boolean} true if a tree was loaded, false otherwise
	 * @private
	 */
	_fetchRollupPrincipals: function () {

		if (!this._rollupTreeId) {
			// Can't add a permission until we have a tree selection
			_.invoke(this.directPanel, 'toggleAddPrincipal', false)
			return false
		}

		const url = this._getRollupTreePermissionsUrl()
		this.loadingDots.render()
		_.each(this.panels, p => $(p.el).addClass('is-not-displayed'))
		$.get(url, principalsByGrant => {
			this.loadingDots.clear()
			_.each(this.panels, p => $(p.el).removeClass('is-not-displayed'))

			const direct = principalsByGrant['direct'] || []
			const indirect = principalsByGrant['indirect'] || []
			this.directPanel.updatePrincipals(direct)
			this.indirectPanel.updatePrincipals(indirect)

			// Even if there are no principals, the user should be able to add one
			this.directPanel.toggleAddPrincipal()
			this._toggleRollupElements()

			_.invoke(this._treeItemSelector, 'trigger', 'done')
		})

		return true
	},

	_getRollupTreePermissionsUrl() {
		// #27261, #27966 VLFs need to get the underlyingId,
		// but linked fields should NOT follow the linked dataset
		let fieldId = this._fieldId
		if (Dataset.isVisibleLinkedFieldIdentifier(fieldId)) {
			fieldId = this._field().id
		}
		return `/api/datasets/${this.model.id}/trees/${this._rollupTreeId}` +
			`/field-permissions/${fieldId}`
	},

	/**
	 * If a filter can be constructed from the selects this method returns it in
	 * an array such that it can be assigned to permissions.filters
	 * @private
	 */
	_readFiltersFromDom: function(principal) {
		const {id, type, fieldId, treeRootId} = principal
		const principalFilters = _.get(this.options.principalModelFn(id, type, fieldId), 'permissions.filters')
		let filters = []
		if (this._isRollupTreeFilter()) { // selection: Rollup Field
			filters = this._createRollupFilter(principal, principalFilters)
		} else if (this._filterIdentity) { // selection: Non-Rollup Field
			filters = this._createNonRollupFilter(principal, principalFilters)
		} else if (this._isRollupTreeGroupSelectedAsField()) { // selection: Rollup Tree Template
			filters.push(permissionsUtil.newBuiltInRollupFilter(
				principal, treeRootId, fieldId))
		}
		return filters
	},

	_createRollupFilter: function (principal, principalFilters) {
		const {type, fieldId, treeRootId} = principal
		let filters = []

		const isNewTemplatePermission = () => _.isNil(_
			.find(principalFilters, Dataset.isBuiltInFilter))

		if (type === Dataset.ROLLUP_TREE_GROUP && isNewTemplatePermission()) {
			// The user just chose a rollup template principal that hasn't ever been
			// assigned to this dataset; so create the built-in rollup filter first
			filters.push(permissionsUtil.newBuiltInRollupFilter(principal,
				treeRootId, fieldId))
		}
		filters.push(permissionsUtil.newRollupFilter(
			this._rollupTreeId, this._fieldId,
			nextLinkGroup(principalFilters)))

		return filters
	},

	_createNonRollupFilter: function(principal, principalFilters) {
		const {type, fieldId, treeRootId} = principal
		let filters = []
		const selectedFilter = this._filterIdentity.toJSON()
		if (type === Dataset.ROLLUP_TREE_GROUP) {
			// The user just added a new Tree Template to an existing dataset filter selection
			// First we need to add the built-in rollup filter
			filters.push(permissionsUtil.newBuiltInRollupFilter(
				principal, treeRootId, fieldId))
			// Only now can we add the current filter, AND'd to the built-in
			selectedFilter.linkGroup = nextLinkGroup(filters)
		} else {
			selectedFilter.linkGroup = nextLinkGroup(principalFilters)
		}
		filters.push(selectedFilter)
		return filters
	},

	getViewState() {
		const state = {
			scrollTop: this.options.scrollContainer.scrollTop()
		}
		_.forEach(this.panels, p => state[p.id] = p.getViewState())
		return state
	},

	// Cleanup

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this._removeChildren()
	},

	_removeChildren: function() {
		_.invokeMap(this.panels, 'remove')
		_.invokeMap([this._treeItemSelector, this.loadingDots],'remove')
	}

})

export default PermissionsByFilter

PermissionsByFilter.__filename__ = 'datasets/edit/permissions-by-filter.js'
